import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import { useAnalyticsConsent } from '../contexts/AnalyticsContext';
import { IS_ANALYTICS_ENABLED } from './config';
import { EventCategory } from './events';

/**
 * Button Tracker Component
 *
 * Automatically tracks all button clicks using event delegation.
 * Captures button text, location, action type, and associated IDs.
 */
export const ButtonTracker: React.FC = () => {
  const location = useLocation();
  const { hasConsent } = useAnalyticsConsent();
  const lastClickTime = useRef<number>(0);
  const lastClickTarget = useRef<Element | null>(null);

  useEffect(() => {
    if (!IS_ANALYTICS_ENABLED || !hasConsent) {
      return;
    }

    const handleClick = (event: MouseEvent) => {
      try {
        const target = event.target as HTMLElement;
        if (!target) return;

        // Find the button element (could be the target or a parent)
        const button = target.closest('button, a[role="button"], input[type="button"], input[type="submit"]');
        if (!button) return;

        // Skip if explicitly marked to skip tracking
        if (button.getAttribute('data-analytics-skip') === 'true') {
          return;
        }

        // Debounce: prevent tracking the same button within 300ms
        const now = Date.now();
        if (
          lastClickTarget.current === button &&
          now - lastClickTime.current < 300
        ) {
          return;
        }
        lastClickTime.current = now;
        lastClickTarget.current = button;

        // Extract button metadata
        const buttonText = extractButtonText(button);
        const buttonLocation = extractButtonLocation(button);
        const buttonAction = button.getAttribute('data-analytics-action') || undefined;
        const buttonId = extractButtonId(button);
        const customLabel = button.getAttribute('data-analytics-label') || undefined;

        // Extract all data-analytics-* attributes
        const customAttributes: Record<string, string> = {};
        Array.from(button.attributes).forEach((attr) => {
          if (attr.name.startsWith('data-analytics-') &&
              !['data-analytics-skip', 'data-analytics-location', 'data-analytics-action',
                'data-analytics-id', 'data-analytics-label'].includes(attr.name)) {
            const key = attr.name.replace('data-analytics-', '');
            customAttributes[key] = attr.value;
          }
        });

        // Track the button click event
        ReactGA.event('button_click', {
          category: EventCategory.INTERACTION,
          label: customLabel || buttonText,
          button_text: buttonText,
          button_location: buttonLocation,
          button_action: buttonAction,
          button_id: buttonId,
          page_path: location.pathname,
          ...customAttributes,
        });
      } catch (error) {
        console.error('Failed to track button click:', error);
      }
    };

    // Add event listener to document for event delegation
    document.addEventListener('click', handleClick, true);

    // Cleanup
    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [hasConsent, location.pathname]);

  return null; // This component only handles side effects
};

/**
 * Extract button text from element
 */
function extractButtonText(button: Element): string {
  // Try aria-label first (for icon buttons)
  const ariaLabel = button.getAttribute('aria-label');
  if (ariaLabel) {
    return ariaLabel;
  }

  // Try data-analytics-label
  const customLabel = button.getAttribute('data-analytics-label');
  if (customLabel) {
    return customLabel;
  }

  // Get text content, but limit length and clean whitespace
  const textContent = button.textContent?.trim() || '';
  const cleanText = textContent.replace(/\s+/g, ' ').substring(0, 100);

  return cleanText || 'unlabeled_button';
}

/**
 * Extract button location from data attribute or DOM structure
 */
function extractButtonLocation(button: Element): string {
  // Try data-analytics-location first
  const locationAttr = button.getAttribute('data-analytics-location');
  if (locationAttr) {
    return locationAttr;
  }

  // Try to find parent with an ID
  let parent = button.parentElement;
  let depth = 0;
  while (parent && depth < 5) {
    const id = parent.id;
    if (id) {
      return id;
    }

    // Check for common container classes
    const className = parent.className;
    if (typeof className === 'string') {
      const relevantClasses = [
        'MuiDialog',
        'MuiDrawer',
        'MuiCard',
        'MuiPaper',
      ];
      for (const relevantClass of relevantClasses) {
        if (className.includes(relevantClass)) {
          return relevantClass;
        }
      }
    }

    parent = parent.parentElement;
    depth++;
  }

  return 'unknown';
}

/**
 * Extract associated entity ID from data attributes
 */
function extractButtonId(button: Element): string | undefined {
  // Check common ID attribute patterns
  const idAttr = button.getAttribute('data-analytics-id') ||
                 button.getAttribute('data-case-id') ||
                 button.getAttribute('data-client-id') ||
                 button.getAttribute('data-id');

  return idAttr || undefined;
}
